import { inject, Injectable } from '@angular/core';
import { catchError, defer, EMPTY, finalize, map, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ChatLibraryDateRange, CreativeCardProps } from '../models/chat-library.models';
import { CreativeCardPayload } from '../../../my-brand-screen/types';
import { CreativeDataPayload } from '../../../social-overview-screen-a/types';
import { StateService } from '../../../../services/state.service';
import { DashboardService } from '../../../../services/dashboard.service';
import { BrandsService } from '../../../../services/brands.service';
import { SocialOverviewScreenService } from '../../../social-overview-screen-a/social-overview-screen.service';
import {
  SocialCreativeCardModalComponent
} from '../../../social-overview-screen-a/creative-gallery/creative-card-modal/social-creative-card-modal.component';
import {
  CreativeCardModalComponent
} from '../../../my-brand-screen/creative-gallery/creative-card-modal/creative-card-modal.component';
import { UtilService } from '../../../../services/util.service';
import { DateRangeValue, getLastNumDays } from '../../../../utils/time';
import { isoDateStringToLocalDate } from '../../../../utils/date';

const FALLBACK_RANGE_DAYS = 7;
const CREATIVE_LOAD_ERROR = 'Creative failed to load. Please try again.';

@Injectable({providedIn: 'root'})
export class ChatLibraryCreativeModalService {
  private readonly stateService = inject(StateService);
  private readonly dashboardService = inject(DashboardService);
  private readonly brandsService = inject(BrandsService);
  private readonly socialOverviewScreenService = inject(SocialOverviewScreenService);
  private readonly dialog = inject(MatDialog);
  private readonly utilService = inject(UtilService);

  public openCreativeModal(card: CreativeCardProps, dateRange?: ChatLibraryDateRange): Observable<void> {
    if (card.source === 'social') {
      return this.openSocialModal(card, dateRange);
    }
    return this.openOpenWebModal(card, dateRange);
  }

  public buildSocialPayload(card: CreativeCardProps, dateRange?: ChatLibraryDateRange): CreativeDataPayload {
    const {startDate, endDate} = this.resolveDateRange(dateRange);
    return {
      brandKey: card.brandKey,
      creativeId: card.creativeId,
      platforms: card.platforms,
      startDate,
      endDate,
    };
  }

  public buildOpenWebPayload(card: CreativeCardProps, dateRange?: ChatLibraryDateRange): CreativeCardPayload {
    const {startDate, endDate} = this.resolveDateRange(dateRange);
    return {
      brandsKeys: [card.brandKey],
      creativeId: card.creativeId,
      accountKey: this.stateService.activeAccount(),
      countries: [],
      includeOtherCountries: false,
      devices: [],
      startDate,
      endDate,
    };
  }

  private openSocialModal(card: CreativeCardProps, dateRange?: ChatLibraryDateRange): Observable<void> {
    const payload = this.buildSocialPayload(card, dateRange);
    return defer(() => {
      this.stateService.loading = true;
      return this.socialOverviewScreenService.getCreativeData(payload);
    }).pipe(
      finalize(() => this.stateService.loading = false),
      map(result => {
        if (!result?.modalData) {
          this.showLoadError();
          return;
        }
        this.dialog.open(SocialCreativeCardModalComponent, {
          data: {
            ...result.modalData,
            graphData: result.graphData,
            logoPath: this.dashboardService.getBrandIcon(card.brandKey),
            brandName: card.brandName,
            creativeUrl: card.thumbnailUrl ?? card.creativeUrl,
            videoUrl: result.modalData.creativeUrl,
            rangeStart: payload.startDate,
            rangeEnd: payload.endDate,
          },
          panelClass: ['creative-card-modal', 'social-creative-card-modal'],
          autoFocus: false
        });
      }),
      catchError(() => {
        this.showLoadError();
        return EMPTY;
      })
    );
  }

  private openOpenWebModal(card: CreativeCardProps, dateRange?: ChatLibraryDateRange): Observable<void> {
    const payload = this.buildOpenWebPayload(card, dateRange);
    return defer(() => {
      this.stateService.loading = true;
      return this.isMyBrand(card.brandKey)
        ? this.brandsService.getMyCreativeCard(payload)
        : this.brandsService.getCompetitorCreativeCard(payload);
    }).pipe(
      finalize(() => this.stateService.loading = false),
      map(result => {
        if (!result) {
          this.showLoadError();
          return;
        }
        this.dialog.open(CreativeCardModalComponent, {
          data: {...result, hideNavigation: true},
          panelClass: 'creative-card-modal',
          autoFocus: false
        });
      }),
      catchError(() => {
        this.showLoadError();
        return EMPTY;
      })
    );
  }

  private showLoadError(): void {
    this.utilService.openSnackBar(CREATIVE_LOAD_ERROR, {variant: 'error'});
  }

  private isMyBrand(brandKey: string): boolean {
    return (this.dashboardService.myBrandsAndCompetitors() ?? [])
      .some(entry => entry.myBrand.key === brandKey);
  }

  private resolveDateRange(dateRange?: ChatLibraryDateRange): DateRangeValue {
    const startDate = isoDateStringToLocalDate(dateRange?.startDate);
    const endDate = isoDateStringToLocalDate(dateRange?.endDate);
    if (!startDate || !endDate) {
      return getLastNumDays(FALLBACK_RANGE_DAYS);
    }
    return {startDate, endDate};
  }
}
